import isNode from 'detect-node';
import queryString from 'query-string';

import {GabaritVisualisation} from 'Libs/algo_flags';

function getParameterByName(name: string): string {
  if (typeof location !== 'undefined') {
    const query = queryString.parse(location.search);
    const param = query[name];
    if (typeof param === 'string') {
      return param;
    }
  }
  // console.log('query vars not supported');
  // Nodejs or workers: location doesn't exist
  // read command line arguments ?
  // https://nodejs.org/docs/latest/api/process.html#process_process_argv
  return '';
}

// defaults to be kept in sync with the values in user_faisability.finish() in buildrz-web...
const autoResultsFilters = {
  AUTO_RESULTS_PERCENTILE_THRESHOLDS_PER_TENEMENT_AREA:
    getParameterByName(
      'auto_results_percentile_thresholds_per_tenement_area',
    ) || '[[1500, 95], 90]',
  AUTO_RESULTS_MIN_COUNT_PER_TENEMENT:
    getParameterByName('auto_results_min_count_per_tenement') || '10',
  AUTO_RESULTS_MAX_COUNT_PER_TENEMENT:
    getParameterByName('auto_results_max_count_per_tenement') || '100',
};

const options = {
  //general purpose
  SIMPLEBUILDINGS: getParameterByName('simplebuildings'),
  CHECK_FORM: getParameterByName('form'),
  NUM_TILES: (() => {
    const tiles = parseInt(getParameterByName('tiles'), 10);
    return Number.isFinite(tiles) ? tiles : 1;
  })(),
  HD: getParameterByName('hd'),
  UHD: getParameterByName('uhd'),
  NOPLU: getParameterByName('noplu'),
  IS_HEADLESS: getParameterByName('headless'),
  CHAPTERS: getParameterByName('chapters'),

  // general purpose: PLU auto
  NO_SIZE_LIMIT: getParameterByName('nosizelimit'),
  CLEARABLE_PLU_AUTO_RESULTS: getParameterByName('clearauto'),
  NUM_PLU_RESULTS: (() => {
    const num = parseInt(getParameterByName('numres'), 10);
    return Number.isFinite(num) ? num : 10;
  })(),
  NUM_LAMBDA_ITERATIONS: (() => {
    const num = parseInt(getParameterByName('iter'), 10);
    if (Number.isFinite(num)) {
      return num;
    } else {
      return getParameterByName('randseed') ? 1 : null;
    }
  })(),
  VIS_A_VIS_COEFF: (() => {
    const num = parseInt(getParameterByName('visaviscoeff'), 10);
    return Number.isFinite(num) ? num : 0;
  })(),
  ARTIF_COEFF: (() => {
    const num = parseInt(getParameterByName('artifcoeff'), 10);
    return Number.isFinite(num) ? num : 0;
  })(),
  SUN_COEFF: (() => {
    const num = parseInt(getParameterByName('suncoeff'), 10);
    return Number.isFinite(num) ? num : 0;
  })(),
  SHADOW_COEFF: (() => {
    const num = parseInt(getParameterByName('shadowcoeff'), 10);
    return Number.isFinite(num) ? num : 0;
  })(),
  HARMONY_COEFF: (() => {
    const num = parseInt(getParameterByName('haromnycoeff'), 10);
    return Number.isFinite(num) ? num : 0;
  })(),
  PIGNON_COEFF: (() => {
    const num = parseInt(getParameterByName('pignoncoeff'), 10);
    return Number.isFinite(num) ? num : 0;
  })(),
  SDP_COEFF: (() => {
    const num = parseInt(getParameterByName('sdpcoeff'), 10);
    return Number.isFinite(num) ? num : 0;
  })(),
  COMPLEXITY_COEFF: (() => {
    const num = parseInt(getParameterByName('complexitycoeff'), 10);
    return Number.isFinite(num) ? num : 0;
  })(),
  NO_BG_DIVERSITY: getParameterByName('nobgdiversity'),
  SORT_BY_SP: getParameterByName('sortbysp'),
  SIMILARITY_RATIO: (() => {
    const num = parseFloat(getParameterByName('similarityratio'));
    return Number.isFinite(num) ? num : 0.91;
  })(),
  ANGLE_WEIGHT: (() => {
    const num = parseInt(getParameterByName('angleweight'), 10);
    return Number.isFinite(num) ? num : 0;
  })(),
  SQUARE_ANGLES: getParameterByName('squareangles'),
  IGNORE_PARCEL_POINTS: getParameterByName('ignoreparcelpoints'),
  MAX_INFLEXION_ANGLE: parseInt(getParameterByName('maxinflectionangle'), 10),
  COMPUTE_BOM_CARBON_COSTS: getParameterByName('computebom'),
  VERBOUS_INDICATORS: getParameterByName('verbousindicators'),
  VERBOUS_ENERGY: getParameterByName('verbousenergy'),
  VERBOUS_COSTS: getParameterByName('verbouscosts'),
  VERBOUS_CONSTRUCTION: getParameterByName('verbousconstruction'),
  ...autoResultsFilters,

  //debug algo
  TEST_USINE: getParameterByName('testusine'),
  PLU_AUTO_SEED: (() => {
    const seed = parseInt(getParameterByName('randseed'), 10);
    return Number.isFinite(seed) ? seed : null;
  })(),
  LAMBDA_SUFFIX: getParameterByName('lambdasuffix'),
  MAX_ITER_DURATION: parseInt(getParameterByName('maxseciter'), 10),
  MAX_FAISA_DURATION: parseInt(getParameterByName('maxsecfaisa'), 10),
  ARCHITECTURE_SEQUENCE: getParameterByName('architecturesequence'),
  ALGO_LOGS: (() => {
    const withAlgoLogs = getParameterByName('withlogs');
    return (
      withAlgoLogs &&
      (withAlgoLogs.toLowerCase() === 'true' || withAlgoLogs === '1')
    );
  })(),
  GABARITS_VISUALISATION: (() => {
    const gabarits = getParameterByName('gabarits');
    switch (gabarits) {
      case 'all':
        return GabaritVisualisation.ALL;
      case 'false':
        return GabaritVisualisation.FALSY;
      case 'none':
        return GabaritVisualisation.NONE;
      default:
        return GabaritVisualisation.NONE;
    }
  })(),
  FORCED_CVS_IN_IC: parseInt(getParameterByName('cvinic'), 10),

  // debug 3D
  PLU_MODE: getParameterByName('plu'), // ???
  LIGHT: getParameterByName('light'),
  NOSHADOWS: getParameterByName('noshadows'),
  NOREFLECTIONS: getParameterByName('noreflections'),
  NOTERRAIN: getParameterByName('noterrain'),
  NOSKY: getParameterByName('nosky'),
  NOBUILDINGS: getParameterByName('nobuildings'),
  NOSCENERY: getParameterByName('noscenery'),
  SOFTSHADOWS: getParameterByName('softshadows'),
  SCENERY_EDGES: getParameterByName('sedges'),
  MODEL_EDGES: getParameterByName('medges'),
  NO_POIS: getParameterByName('nopois'),

  //features one shot that seem abandonned
  POI_DEPTH_TEST: getParameterByName('trans'),

  MAXDEPTH30: getParameterByName('maxdepth30'),
  MAX_BUILDINGS: getParameterByName('maxBuildings'),
};

if (!isNode && options.NUM_LAMBDA_ITERATIONS) {
  console.log(
    `%cBuilding generator forced iterations count: ${options.NUM_LAMBDA_ITERATIONS}`,
    'color: #39B2E6',
  );
}

export default options;
